import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { X, ChevronRight, Lightbulb, MessageCircle } from 'lucide-react';

const API = `${window.location.origin}/api`;
const MASCOT = '/sidekick.png';
const FAB = 56;

const TIPS = {
  customer: [
    'Add a profile photo so your providers recognize you on the day.',
    'Save multiple addresses (home, office) for faster booking next time.',
    'Check provider ratings and reviews before you book.',
    'Turn on notifications so you never miss a booking update.',
    'Add clear notes to your booking so the provider arrives prepared.',
    'Found a great provider? Rate them to help your neighbors too!',
  ],
  provider: [
    'Add clear photos of your past work — it wins more bookings.',
    'Keep your availability updated so customers book the right slots.',
    'Expand your service areas to appear in more local searches.',
    'Reply to messages quickly to boost your rating and ranking.',
    'Complete your bio with your experience to build customer trust.',
    'Great job done? Ask happy customers for a review!',
  ],
};

function ProgressRing({ percent, children }) {
  const r = 20;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, percent)) / 100) * c;
  const done = percent >= 100;
  return (
    <span className="relative inline-flex items-center justify-center w-12 h-12">
      <svg className="absolute inset-0 -rotate-90" width="48" height="48" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r={r} fill="none" stroke="#e7e5e4" strokeWidth="3.5" />
        <circle
          cx="24" cy="24" r={r} fill="none"
          stroke={done ? '#10b981' : '#f59e0b'} strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset .6s ease' }}
        />
      </svg>
      {children}
    </span>
  );
}

function clampPos(p) {
  const vw = window.innerWidth, vh = window.innerHeight;
  return {
    left: Math.min(Math.max(8, p.left), vw - FAB - 8),
    top: Math.min(Math.max(70, p.top), vh - FAB - 90),
  };
}

export default function Sidekick() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem('sidekick_hidden') === '1');
  const [pos, setPos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('juan_pos')); if (s) return clampPos(s); } catch (_) {}
    return { left: window.innerWidth - FAB - 16, top: window.innerHeight - FAB - 180 };
  });

  const dragRef = useRef({ dragging: false, moved: false, offX: 0, offY: 0, startX: 0, startY: 0 });

  const authHeaders = { Authorization: `Bearer ${token || localStorage.getItem('token')}` };

  const eligible = user && (user.role === 'customer' || user.role === 'provider');
  const onHome = location.pathname === '/home' || location.pathname === '/provider';

  const check = useCallback(async () => {
    if (!eligible) { setData(null); return; }
    try {
      const res = await axios.get(`${API}/profile/completeness`, { headers: authHeaders });
      setData(res.data || {});
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, token]);

  useEffect(() => { check(); }, [check, location.pathname]);

  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p));
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // ---- drag handlers ----
  const onPointerDown = (e) => {
    const d = dragRef.current;
    d.dragging = true; d.moved = false;
    d.offX = e.clientX - pos.left; d.offY = e.clientY - pos.top;
    d.startX = e.clientX; d.startY = e.clientY;
    e.target.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    const next = clampPos({ left: e.clientX - d.offX, top: e.clientY - d.offY });
    if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 4) d.moved = true;
    setPos(next);
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    if (!d.dragging) return;
    d.dragging = false;
    localStorage.setItem('juan_pos', JSON.stringify(pos));
    if (!d.moved) setOpen((o) => !o);
  };

  if (!eligible || !onHome || !data || hidden) return null;

  const missing = data.missing || [];
  const incomplete = missing.length > 0;
  const percent = data.percent ?? (incomplete ? 0 : 100);
  const role = data.role || 'customer';
  const tipList = TIPS[role] || TIPS.customer;
  const tip = tipList[Math.floor(Date.now() / 86400000) % tipList.length];
  const messagesPath = role === 'provider' ? '/provider/support' : '/messages';

  const hideForSession = () => { sessionStorage.setItem('sidekick_hidden', '1'); setHidden(true); };
  const go = (path) => { setOpen(false); navigate(path); };

  const vw = window.innerWidth, vh = window.innerHeight;
  const panelStyle = {
    position: 'fixed',
    right: Math.max(8, vw - (pos.left + FAB)),
    bottom: Math.max(8, vh - pos.top + 10),
    maxHeight: Math.max(260, pos.top - 20),
  };

  return (
    <>
      {open && (
        <div
          style={panelStyle}
          className="w-[320px] max-w-[calc(100vw-1rem)] max-h-[calc(100vh-7rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden animate-[sidekickIn_.3s_ease] z-[45]"
          data-testid="juan-panel"
        >
          <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-amber-50 to-white flex-shrink-0 border-b border-amber-100">
            <img src={MASCOT} alt="Juan" className="w-10 h-10 rounded-full object-cover object-top ring-2 ring-amber-400 bg-amber-100 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 font-[Poppins]">Juan</p>
              <p className="text-[11px] text-slate-500">Your HandyBayani helper</p>
            </div>
            <button onClick={hideForSession} className="text-[11px] text-slate-400 hover:text-slate-600 font-medium" data-testid="juan-hide" title="Hide until next login">Hide</button>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 p-0.5" data-testid="juan-close" aria-label="Close"><X className="w-4 h-4" /></button>
          </div>

          <div className="p-3 space-y-2 overflow-y-auto flex-1">
            <p className="text-xs text-slate-500 px-1" data-testid="sidekick-profile-status">
              {incomplete ? `Your profile is ${percent}% complete — let's finish up!` : "You're all set! Here's a tip for you."}
            </p>
            {incomplete ? missing.map((m) => (
              <button key={m.key} onClick={() => go(m.cta_path)} className="w-full text-left rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50/60 transition-colors p-3 group" data-testid={`sidekick-item-${m.key}`}>
                <p className="text-sm font-semibold text-slate-800">{m.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">{m.detail}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                  {m.cta_label} <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </button>
            )) : (
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-3" data-testid="sidekick-tip">
                <p className="text-xs font-bold text-amber-700 flex items-center gap-1.5 mb-1"><Lightbulb className="w-4 h-4" /> Tip of the day</p>
                <p className="text-sm text-slate-700 leading-snug">{tip}</p>
              </div>
            )}

            <button
              onClick={() => go(messagesPath)}
              className="w-full mt-1 flex items-center justify-center gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold py-2.5 transition-colors"
              data-testid="sidekick-ask-juan-link"
            >
              <MessageCircle className="w-4 h-4 text-amber-400" /> Ask Juan in Messages
            </button>
          </div>
        </div>
      )}

      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{ position: 'fixed', left: pos.left, top: pos.top, touchAction: 'none' }}
        className="relative rounded-full bg-white shadow-xl active:scale-95 transition-transform z-[46] cursor-grab active:cursor-grabbing"
        data-testid="sidekick-fab"
        aria-label="Open Juan assistant"
      >
        <ProgressRing percent={percent}>
          <span className="w-9 h-9 rounded-full overflow-hidden bg-amber-100 pointer-events-none">
            <img src={MASCOT} alt="Juan assistant" className="w-full h-full object-cover object-top" draggable={false} />
          </span>
        </ProgressRing>
        {incomplete && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-[20px] px-1 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center ring-2 ring-white pointer-events-none" data-testid="sidekick-badge">
            {missing.length}
          </span>
        )}
      </button>

      <style>{`@keyframes sidekickIn{from{opacity:0;transform:translateY(12px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </>
  );
}
